import { useState, useEffect, useMemo, useCallback } from "react";
import {
  ComposableMap,
  Geographies,
  Geography,
  Marker,
} from "react-simple-maps";
import { geoCentroid, geoBounds, geoContains } from "d3-geo";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "./ui/sheet";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { useAuth, API } from "../App";
import {
  MapPin, Users, AlertCircle, Building2, Mail, Phone, ExternalLink, X,
  GraduationCap, Loader2,
} from "lucide-react";

// Public US-states topojson (state-level shapes). Hosted by US Atlas (us-atlas v3).
const US_TOPO_URL = "https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json";

// FIPS (numeric, as zero-padded string) → 2-letter postal code
const FIPS_TO_CODE = {
  "01":"AL","02":"AK","04":"AZ","05":"AR","06":"CA","08":"CO","09":"CT","10":"DE",
  "11":"DC","12":"FL","13":"GA","15":"HI","16":"ID","17":"IL","18":"IN","19":"IA",
  "20":"KS","21":"KY","22":"LA","23":"ME","24":"MD","25":"MA","26":"MI","27":"MN",
  "28":"MS","29":"MO","30":"MT","31":"NE","32":"NV","33":"NH","34":"NJ","35":"NM",
  "36":"NY","37":"NC","38":"ND","39":"OH","40":"OK","41":"OR","42":"PA","44":"RI",
  "45":"SC","46":"SD","47":"TN","48":"TX","49":"UT","50":"VT","51":"VA","53":"WA",
  "54":"WV","55":"WI","56":"WY","72":"PR",
};

// Aspiritech brand palette for the choropleth.
const COLOR_EMPTY = "#E2E8F0";   // slate-200
const COLOR_HOVER = "#FFD507";   // brand gold
const RAMP = ["#DBE3F4", "#A6B6DF", "#6F84C2", "#3A55A4", "#0F1B3D"]; // navy ramp

function pickColor(count, max) {
  if (!count || count <= 0) return COLOR_EMPTY;
  if (max <= 0) return RAMP[0];
  const ratio = count / max;
  if (ratio >= 0.8) return RAMP[4];
  if (ratio >= 0.6) return RAMP[3];
  if (ratio >= 0.4) return RAMP[2];
  if (ratio >= 0.2) return RAMP[1];
  return RAMP[0];
}

// Deterministic PRNG so dots don't shuffle between renders.
function mulberry32(seed) {
  let t = seed >>> 0;
  return function () {
    t = (t + 0x6D2B79F5) >>> 0;
    let r = t;
    r = Math.imul(r ^ (r >>> 15), r | 1);
    r ^= r + Math.imul(r ^ (r >>> 7), r | 61);
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
}

function seedFromString(s) {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

// Visual cap so a 124-student state doesn't choke render or overwhelm the eye.
const MAX_PINS_PER_STATE = 80;

// ─────────────────────────────────────────────────────────────────────
// Sheet contents: state-detail with Students / Workforce Boards tabs
// ─────────────────────────────────────────────────────────────────────
const StateDetailSheet = ({ stateCode, stateName, onClose }) => {
  const { token } = useAuth();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!stateCode) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setDetail(null);
    const load = async () => {
      try {
        const res = await fetch(`${API}/stats/admin/student-locations/${stateCode}`, {
          credentials: "include",
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (!cancelled) setDetail(json);
      } catch (e) {
        if (!cancelled) setError(e.message || "Failed to load");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [stateCode, token]);

  const open = Boolean(stateCode);

  return (
    <Sheet open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-2xl bg-white p-0 flex flex-col"
        data-testid="state-detail-sheet"
      >
        <SheetHeader className="px-6 py-4 border-b border-slate-200 bg-brand-navy text-white">
          <div className="flex items-center justify-between gap-2">
            <div>
              <SheetTitle className="text-white text-xl font-heading flex items-center gap-2">
                <MapPin className="w-5 h-5 text-brand-gold" />
                {stateName || stateCode}
              </SheetTitle>
              <SheetDescription className="text-white/80 mt-1">
                {loading ? "Loading enrolled students & workforce boards…"
                  : error ? "Unable to load details"
                  : `${detail?.student_count ?? 0} enrolled • ${detail?.boards?.length ?? 0} workforce board${detail?.boards?.length === 1 ? "" : "s"}`}
              </SheetDescription>
            </div>
            <button
              onClick={onClose}
              className="rounded-lg p-1.5 hover:bg-white/10 transition"
              data-testid="state-sheet-close"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </SheetHeader>

        <div className="flex-1 overflow-hidden flex flex-col">
          {loading ? (
            <div className="flex-1 flex items-center justify-center gap-2 text-slate-500">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          ) : error ? (
            <div className="flex-1 flex items-center justify-center gap-2 text-red-600" data-testid="state-sheet-error">
              <AlertCircle className="w-4 h-4" /> {error}
            </div>
          ) : (
            <Tabs defaultValue="students" className="flex-1 flex flex-col overflow-hidden">
              <TabsList className="mx-6 mt-4 bg-surface-muted border border-slate-200 self-start">
                <TabsTrigger
                  value="students"
                  className="data-[state=active]:bg-brand-navy data-[state=active]:text-white gap-2"
                  data-testid="state-tab-students"
                >
                  <GraduationCap className="w-4 h-4" /> Students ({detail?.student_count ?? 0})
                </TabsTrigger>
                <TabsTrigger
                  value="boards"
                  className="data-[state=active]:bg-brand-navy data-[state=active]:text-white gap-2"
                  data-testid="state-tab-boards"
                >
                  <Building2 className="w-4 h-4" /> Workforce Boards ({detail?.boards?.length ?? 0})
                </TabsTrigger>
              </TabsList>

              {/* STUDENTS */}
              <TabsContent value="students" className="flex-1 overflow-y-auto px-6 py-4 mt-0">
                {(!detail?.students || detail.students.length === 0) ? (
                  <div className="text-center text-slate-500 py-12" data-testid="state-students-empty">
                    No enrolled students in {stateName} yet.
                  </div>
                ) : (
                  <div className="space-y-2" data-testid="state-students-list">
                    {detail.students.map((s) => (
                      <div
                        key={s.user_id}
                        className="border border-slate-200 rounded-xl px-4 py-3 hover:border-brand-navy/40 hover:bg-slate-50 transition"
                        data-testid={`state-student-${s.user_id}`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="font-medium text-slate-900 truncate">{s.name}</p>
                            <p className="text-xs text-slate-500 truncate">{s.email}</p>
                            <p className="text-xs text-slate-400 mt-1">
                              {[s.city, s.zip].filter(Boolean).join(" · ") || "—"}
                            </p>
                          </div>
                          <Badge variant="outline" className="text-xs shrink-0 capitalize">
                            {(s.status || "").replace(/_/g, " ")}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>

              {/* BOARDS */}
              <TabsContent value="boards" className="flex-1 overflow-y-auto px-6 py-4 mt-0">
                {!detail?.wdb_configured ? (
                  <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-amber-900 text-sm flex items-start gap-2" data-testid="state-boards-not-configured">
                    <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                    <span>
                      CareerOneStop API is not configured on this environment.
                      Workforce-board lookups are disabled.
                    </span>
                  </div>
                ) : (!detail?.boards || detail.boards.length === 0) ? (
                  <div className="text-center text-slate-500 py-12" data-testid="state-boards-empty">
                    No workforce boards found for the {detail?.unique_zip_count ?? 0} unique ZIPs in {stateName}.
                  </div>
                ) : (
                  <div className="space-y-3" data-testid="state-boards-list">
                    <p className="text-xs text-slate-500" data-testid="state-boards-meta">
                      Looked up across {detail.unique_zip_count} unique ZIP{detail.unique_zip_count === 1 ? "" : "s"} ·
                      Showing {detail.boards.length} distinct board{detail.boards.length === 1 ? "" : "s"}.
                    </p>
                    {detail.boards.map((b, i) => (
                      <div
                        key={`${b.id || ""}-${i}`}
                        className="border border-slate-200 rounded-xl p-4 hover:border-brand-navy/40 transition"
                        data-testid={`state-board-${i}`}
                      >
                        <div className="flex items-start justify-between gap-3 mb-2">
                          <div className="min-w-0">
                            <p className="font-heading font-bold text-slate-900 text-sm leading-snug">
                              {b.name || "Unnamed Board"}
                            </p>
                            <p className="text-xs text-slate-500 mt-0.5">
                              {[b.address1, b.city, b.state, b.zip].filter(Boolean).join(", ") || "—"}
                            </p>
                          </div>
                          <Badge className="bg-brand-navy/5 text-brand-navy hover:bg-brand-navy/10 shrink-0">
                            {b.level === "STATE" ? "State" : "Local"}
                          </Badge>
                        </div>

                        {(b.contacts && b.contacts.length > 0) && (
                          <div className="mt-2 pt-2 border-t border-slate-100 space-y-1.5">
                            {b.contacts.slice(0, 3).map((c, ci) => (
                              <div key={ci} className="text-xs text-slate-600">
                                {c.name && <span className="font-medium text-slate-800">{c.name}</span>}
                                {c.title && <span className="text-slate-500"> · {c.title}</span>}
                                <div className="flex flex-wrap gap-x-3 gap-y-1 mt-0.5">
                                  {c.email && (
                                    <a href={`mailto:${c.email}`} className="text-brand-navy hover:underline flex items-center gap-1">
                                      <Mail className="w-3 h-3" /> {c.email}
                                    </a>
                                  )}
                                  {c.phone && (
                                    <span className="text-slate-500 flex items-center gap-1">
                                      <Phone className="w-3 h-3" /> {c.phone}
                                    </span>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        {b.url && (
                          <div className="mt-3">
                            <a
                              href={b.url.startsWith("http") ? b.url : `https://${b.url}`}
                              target="_blank"
                              rel="noreferrer noopener"
                              className="text-xs text-brand-navy hover:underline inline-flex items-center gap-1 font-medium"
                            >
                              Visit website <ExternalLink className="w-3 h-3" />
                            </a>
                          </div>
                        )}
                      </div>
                    ))}
                    <p className="text-[10px] text-slate-400 pt-2 border-t border-slate-100">
                      Workforce board data provided by CareerOneStop, sponsored by the
                      U.S. Department of Labor, Employment and Training Administration.
                    </p>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

// ─────────────────────────────────────────────────────────────────────
// Main map component
//   props.publicMode → if true, fetches the no-PII public endpoint,
//   hides the drill-down sheet, and uses softer marketing copy.
// ─────────────────────────────────────────────────────────────────────
const StudentLocationsMap = ({ publicMode = false } = {}) => {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tooltip, setTooltip] = useState(null); // {x, y, name, count, code}
  const [openState, setOpenState] = useState(null); // {code, name}

  useEffect(() => {
    let cancelled = false;
    const endpoint = publicMode
      ? `${API}/public/student-locations`
      : `${API}/stats/admin/student-locations`;
    const load = async () => {
      try {
        const res = await fetch(endpoint, {
          credentials: publicMode ? "omit" : "include",
          headers: (!publicMode && token) ? { Authorization: `Bearer ${token}` } : undefined,
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (!cancelled) {
          setData(json);
          setLoading(false);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e.message || "Failed to load");
          setLoading(false);
        }
      }
    };
    load();
    return () => { cancelled = true; };
  }, [token, publicMode]);

  const { byCode, maxCount, topStates, totalWaitlist } = useMemo(() => {
    const map = {};
    let max = 0;
    let waitlistTotal = 0;
    (data?.by_state || []).forEach(s => {
      map[s.code] = s;
      const combined = (s.count || 0) + (s.waitlist || 0);
      if (combined > max) max = combined;
      waitlistTotal += (s.waitlist || 0);
    });
    const sorted = [...(data?.by_state || [])].sort((a, b) => ((b.count || 0) + (b.waitlist || 0)) - ((a.count || 0) + (a.waitlist || 0)));
    return {
      byCode: map,
      maxCount: max,
      topStates: sorted.slice(0, 5),
      totalWaitlist: data?.waitlist_total ?? waitlistTotal,
    };
  }, [data]);

  const handleStateClick = useCallback((code, name) => {
    if (publicMode) return; // public map is read-only
    if (!code) return;
    const entry = byCode[code];
    if (!entry || entry.count <= 0) return; // skip empty states
    setOpenState({ code, name: entry.name || name || code });
    setTooltip(null);
  }, [byCode, publicMode]);

  return (
    <Card className="card-surface" data-testid={publicMode ? "public-locations-map-card" : "student-locations-map-card"}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <div>
          <CardTitle className="flex items-center gap-2 text-slate-900">
            <MapPin className="w-5 h-5 text-brand-navy" />
            {publicMode ? "Where our community lives" : "Student Locations Across the U.S."}
          </CardTitle>
          <p className="text-sm text-slate-500 mt-1">
            {publicMode
              ? `Enrolled participants across ${data?.states_represented ?? "—"} states & territories`
              : "Live state-by-state rollup · click a state for students & workforce boards"}
          </p>
        </div>
        <div className="text-right">
          <div className="text-2xl font-heading font-bold text-brand-navy" data-testid="map-total-enrolled">
            {data?.total_enrolled ?? "—"}
          </div>
          <p className="text-xs text-slate-500 uppercase tracking-wider">Enrolled</p>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="h-64 flex items-center justify-center text-slate-500 text-sm">
            Loading map…
          </div>
        ) : error ? (
          <div className="h-64 flex items-center justify-center gap-2 text-red-600 text-sm" data-testid="map-error">
            <AlertCircle className="w-4 h-4" /> Unable to load location data ({error})
          </div>
        ) : (
          <div className="relative">
            <ComposableMap
              projection="geoAlbersUsa"
              projectionConfig={{ scale: 1000 }}
              width={980}
              height={520}
              style={{ width: "100%", height: "auto" }}
              data-testid="us-map-svg"
            >
              <Geographies geography={US_TOPO_URL}>
                {({ geographies }) => {
                  // Build pin scatter per state (deterministic + capped + polygon-clamped).
                  // Two layers:
                  //   • Gold pins  → enrolled students
                  //   • Orange pins → waitlist demand (sign-ups not yet enrolled)
                  const pins = [];           // enrolled
                  const waitlistPins = [];   // demand
                  geographies.forEach((geo) => {
                    const fips = String(geo.id).padStart(2, "0");
                    const code = FIPS_TO_CODE[fips];
                    const entry = code ? byCode[code] : null;
                    if (!entry) return;
                    const enrolledCount = entry.count || 0;
                    const waitlistCount = entry.waitlist || 0;
                    if (enrolledCount <= 0 && waitlistCount <= 0) return;
                    let centroid, bounds;
                    try {
                      centroid = geoCentroid(geo);
                      bounds = geoBounds(geo);
                    } catch (err) {
                      return;
                    }
                    if (!centroid || !bounds) return;
                    const [[minLon, minLat], [maxLon, maxLat]] = bounds;
                    const rnd = mulberry32(seedFromString(code));
                    const targetForKind = (kind, count, bucket) => {
                      const visible = Math.min(count, MAX_PINS_PER_STATE);
                      let placed = 0;
                      let attempts = 0;
                      let shrinkPass = 0;
                      let halfExt = 0.40;
                      const maxAttemptsPerPin = 60;
                      while (placed < visible && shrinkPass < 4) {
                        const lonRange = Math.max(0.0001, (maxLon - minLon) * halfExt);
                        const latRange = Math.max(0.0001, (maxLat - minLat) * halfExt);
                        while (placed < visible && attempts < visible * maxAttemptsPerPin) {
                          const dx = (rnd() - 0.5) * 2 * lonRange;
                          const dy = (rnd() - 0.5) * 2 * latRange;
                          const point = [centroid[0] + dx, centroid[1] + dy];
                          attempts++;
                          let inside = false;
                          try { inside = geoContains(geo, point); } catch (e) { inside = false; }
                          if (inside) {
                            bucket.push({ key: `${code}-${kind}-${placed}`, coords: point });
                            placed++;
                          }
                        }
                        shrinkPass++;
                        halfExt = halfExt * 0.5;
                        attempts = 0;
                      }
                      while (placed < visible) {
                        const dx = (rnd() - 0.5) * 0.05;
                        const dy = (rnd() - 0.5) * 0.05;
                        bucket.push({ key: `${code}-${kind}-${placed}`, coords: [centroid[0] + dx, centroid[1] + dy] });
                        placed++;
                      }
                    };
                    targetForKind("e", enrolledCount, pins);
                    targetForKind("w", waitlistCount, waitlistPins);
                  });

                  return (
                    <>
                      {geographies.map((geo) => {
                        const fips = String(geo.id).padStart(2, "0");
                        const code = FIPS_TO_CODE[fips];
                        const entry = code ? byCode[code] : null;
                        const enrolled = entry?.count || 0;
                        const waitlist = entry?.waitlist || 0;
                        const combined = enrolled + waitlist;
                        const fill = pickColor(combined, maxCount);
                        const name = geo.properties?.name || entry?.name || code || "—";
                        const isClickable = enrolled > 0 && !publicMode;
                        return (
                          <Geography
                            key={geo.rsmKey}
                            geography={geo}
                            data-testid={`map-state-${code || "x"}`}
                            onMouseEnter={(e) => {
                              setTooltip({
                                x: e.clientX,
                                y: e.clientY,
                                name,
                                count: enrolled,
                                waitlist,
                                code,
                              });
                            }}
                            onMouseMove={(e) => {
                              setTooltip(t => t ? { ...t, x: e.clientX, y: e.clientY } : t);
                            }}
                            onMouseLeave={() => setTooltip(null)}
                            onClick={() => isClickable && handleStateClick(code, name)}
                            style={{
                              default: {
                                fill,
                                stroke: "#FFFFFF",
                                strokeWidth: 0.75,
                                outline: "none",
                              },
                              hover: {
                                fill: isClickable ? COLOR_HOVER : fill,
                                stroke: "#0F1B3D",
                                strokeWidth: 1,
                                outline: "none",
                                cursor: isClickable ? "pointer" : "default",
                              },
                              pressed: { fill: COLOR_HOVER, outline: "none" },
                            }}
                          />
                        );
                      })}

                      {/* Enrolled student pins — gold */}
                      <g data-testid="map-pins-layer" pointerEvents="none">
                        {pins.map((p) => (
                          <Marker key={p.key} coordinates={p.coords}>
                            <circle
                              r={2}
                              fill="#FFD507"
                              stroke="#0F1B3D"
                              strokeWidth={0.6}
                              opacity={0.95}
                            />
                          </Marker>
                        ))}
                      </g>

                      {/* Waitlist demand pins — orange (slightly smaller + open ring so they read as "not yet enrolled") */}
                      <g data-testid="map-waitlist-pins-layer" pointerEvents="none">
                        {waitlistPins.map((p) => (
                          <Marker key={p.key} coordinates={p.coords}>
                            <circle
                              r={2}
                              fill="#FB923C"
                              stroke="#9A3412"
                              strokeWidth={0.6}
                              opacity={0.9}
                            />
                          </Marker>
                        ))}
                      </g>
                    </>
                  );
                }}
              </Geographies>
            </ComposableMap>

            {tooltip && (
              <div
                className="fixed z-50 pointer-events-none rounded-lg bg-brand-navy text-white px-3 py-2 shadow-lg text-xs"
                style={{
                  left: tooltip.x + 12,
                  top: tooltip.y + 12,
                }}
                data-testid="map-tooltip"
              >
                <div className="font-bold text-sm">{tooltip.name}</div>
                <div className="flex items-center gap-1 mt-0.5">
                  <span className="inline-block w-2 h-2 rounded-full bg-brand-gold border border-brand-navy" />
                  <span>{tooltip.count} enrolled</span>
                </div>
                {tooltip.waitlist > 0 && (
                  <div className="flex items-center gap-1 mt-0.5" data-testid="tooltip-waitlist">
                    <span className="inline-block w-2 h-2 rounded-full bg-orange-400 border border-orange-700" />
                    <span>{tooltip.waitlist} on waitlist</span>
                  </div>
                )}
                {tooltip.count > 0 && !publicMode && (
                  <div className="mt-1 pt-1 border-t border-white/20 text-[10px] text-brand-gold">
                    Click to view students &amp; workforce boards
                  </div>
                )}
              </div>
            )}

            {/* Legend + top states */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mt-4 pt-4 border-t border-slate-200">
              <div className="flex items-center gap-4 flex-wrap" data-testid="map-legend">
                <div className="flex items-center gap-1.5">
                  <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
                    <circle cx="7" cy="7" r="3.5" fill="#FFD507" stroke="#0F1B3D" strokeWidth="0.8" />
                  </svg>
                  <span className="text-xs text-slate-500">enrolled</span>
                </div>
                <div className="flex items-center gap-1.5" data-testid="legend-waitlist">
                  <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
                    <circle cx="7" cy="7" r="3.5" fill="#FB923C" stroke="#9A3412" strokeWidth="0.8" />
                  </svg>
                  <span className="text-xs text-slate-500">on waitlist {totalWaitlist > 0 ? `(${totalWaitlist})` : ""}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-slate-500 uppercase tracking-wider">Demand</span>
                  <div className="flex items-center gap-1">
                    <div className="w-4 h-3 rounded-sm" style={{ backgroundColor: COLOR_EMPTY }} />
                    <span className="text-xs text-slate-500">0</span>
                    {RAMP.map((c, i) => (
                      <div key={i} className="w-4 h-3" style={{ backgroundColor: c }} />
                    ))}
                    <span className="text-xs text-slate-500">{maxCount || 0}</span>
                  </div>
                </div>
              </div>

              {topStates.length > 0 && (
                <div className="flex items-center gap-2 flex-wrap" data-testid="map-top-states">
                  <span className="text-xs text-slate-500 uppercase tracking-wider">Top states:</span>
                  {topStates.map(s => (
                    publicMode ? (
                      <span
                        key={s.code}
                        className="inline-flex items-center gap-1 rounded-full bg-brand-navy/5 text-brand-navy px-3 py-1 text-xs font-medium"
                        data-testid={`top-state-${s.code}`}
                      >
                        {s.name} <span className="opacity-60 mx-1">·</span> {s.count}
                      </span>
                    ) : (
                      <Button
                        key={s.code}
                        variant="outline"
                        size="sm"
                        onClick={() => handleStateClick(s.code, s.name)}
                        className="h-7 px-2.5 rounded-full bg-brand-navy/5 border-transparent hover:bg-brand-navy hover:text-white text-brand-navy text-xs font-medium"
                        data-testid={`top-state-${s.code}`}
                      >
                        {s.name} <span className="opacity-60 mx-1">·</span> {s.count}
                      </Button>
                    )
                  ))}
                </div>
              )}
            </div>

            {data?.unknown_location > 0 && (
              <p className="mt-3 text-xs text-slate-500 flex items-center gap-1" data-testid="map-unknown-note">
                <AlertCircle className="w-3 h-3" />
                {data.unknown_location} enrolled {data.unknown_location === 1 ? "participant" : "participants"} have an unknown/unrecognized state.
              </p>
            )}
          </div>
        )}
      </CardContent>

      {!publicMode && (
        <StateDetailSheet
          stateCode={openState?.code}
          stateName={openState?.name}
          onClose={() => setOpenState(null)}
        />
      )}
    </Card>
  );
};

export default StudentLocationsMap;
